import { useEffect, useRef, useState, type FormEvent } from 'react';
import { inquiryApi, type InquiryEnvironment, type InquiryType } from '../../api/inquiries';
import { createScope } from '../scripts/scope';
import { initReveal } from '../scripts/reveal';

const INQUIRY_TYPES: InquiryType[] = ['일반 문의', '소개자료', '도입 상담'];
const ENVIRONMENTS: InquiryEnvironment[] = ['클라우드', '사내 설치·온프레미스', '미정'];
const EMPLOYEE_COUNTS = ['1–20명', '21–100명', '101–500명', '500명 이상'];
const VISIT_ROUTES = ['검색 (네이버·구글 등)', '지인·동료 소개', '온라인 광고', 'SNS·유튜브', '세미나·박람회', '기타'];

/** 전화번호 자동 포맷: 010-1234-5678 / 02-123-4567 */
function formatPhone(val: string): string {
  const d = val.replace(/\D/g, '').slice(0, 11);
  if (d.startsWith('02')) {
    if (d.length <= 2) return d;
    if (d.length <= 5) return d.slice(0, 2) + '-' + d.slice(2);
    if (d.length <= 9) return d.slice(0, 2) + '-' + d.slice(2, 5) + '-' + d.slice(5);
    return d.slice(0, 2) + '-' + d.slice(2, 6) + '-' + d.slice(6, 10);
  }
  if (d.length <= 3) return d;
  if (d.length <= 7) return d.slice(0, 3) + '-' + d.slice(3);
  return d.slice(0, 3) + '-' + d.slice(3, 7) + '-' + d.slice(7);
}

const EMPTY_FORM = {
  companyName: '',
  employeeCount: '',
  name: '',
  phone: '',
  email: '',
  visitRoute: '',
  message: '',
  agreePrivacy: false,
  agreeMarketing: false,
};

/** 문의하기 페이지: 문의 폼 + 진행 과정 + 바로 연락 + 도입 안내 카드 */
export default function ContactPage() {
  const rootRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [inquiryType, setInquiryType] = useState<InquiryType>('일반 문의');
  const [environment, setEnvironment] = useState<InquiryEnvironment | undefined>();
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [toastVisible, setToastVisible] = useState(false);

  useEffect(() => {
    document.title = 'AcXion-On 문의하기';
    const scope = createScope();
    if (rootRef.current) initReveal(rootRef.current, scope, '.pr-rv');
    return () => scope.dispose();
  }, []);

  const setField = <K extends keyof typeof EMPTY_FORM>(key: K, value: (typeof EMPTY_FORM)[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const scrollToForm = (type?: InquiryType) => {
    if (type) setInquiryType(type);
    document.getElementById('ctf')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // 필수 항목 검사: 첫 번째 비어 있는 항목을 흔들고 포커스
    const required = Array.from(formRef.current?.querySelectorAll<HTMLInputElement>('[required]') ?? []);
    const bad = required.find((x) => (x.type === 'checkbox' ? !x.checked : !x.value.trim()));
    if (bad) {
      (bad.type === 'checkbox' ? (bad.nextElementSibling as HTMLElement) : bad).animate(
        [{ transform: 'translateX(0)' }, { transform: 'translateX(-4px)' }, { transform: 'translateX(4px)' }, { transform: 'translateX(0)' }],
        { duration: 240 },
      );
      bad.focus();
      return;
    }

    setSubmitting(true);
    try {
      await inquiryApi.submit({ ...form, inquiryType, environment });
      reset();
      setToastVisible(true);
      setTimeout(() => setToastVisible(false), 4000);
    } catch {
      setError('문의 접수에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(EMPTY_FORM);
    setInquiryType('일반 문의');
    setEnvironment(undefined);
    setSent(false);
  };

  return (
    <div className="pg-contact" ref={rootRef}>
      {/* 문의 접수 완료 토스트 */}
      {toastVisible && (
        <div style={{
          position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999, background: '#22c55e', color: '#fff',
          padding: '14px 28px', borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
          fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10,
          animation: 'ct-toast-in 0.25s ease',
          whiteSpace: 'nowrap',
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
          문의가 접수되었습니다. 영업일 기준 1일 이내에 연락드립니다.
        </div>
      )}
      <style>{`@keyframes ct-toast-in { from { opacity:0; transform:translateX(-50%) translateY(12px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }`}</style>
      <section className="ct-hero">
        <div className="hfx" aria-hidden="true">
          <i className="bl b1" />
          <i className="bl b2" />
          <i className="bl b3" />
          <i className="dg" />
          <i className="sp" style={{ left: '16%', top: '20%' }} />
          <i className="sp" style={{ left: '82%', top: '14%', animationDelay: '-1.5s' }} />
          <i className="sp" style={{ left: '88%', top: '46%', animationDelay: '-3s' }} />
          <i className="sp" style={{ left: '8%', top: '52%', animationDelay: '-2.2s' }} />
        </div>
        <div className="wrap">
          <div className="ct-hd">
            <div className="eb pr-rv">CONTACT</div>
            <h1 className="pr-rv d1">
              우리 조직에 맞는 <em>AI 도입</em>을<br />
              함께 준비하세요
            </h1>
            <p className="pr-rv d1">제품이나 도입 방법에 대해 궁금한 점을 남겨주세요.</p>
          </div>
          <div className="ct-body">
            <div className="ct-form pr-rv d2" id="ctf">
              <form ref={formRef} noValidate onSubmit={handleSubmit}>
                <div className="fh">
                  <small>답변에 필요한 연락처와 문의 내용</small>
                  <h2>문의 정보 입력</h2>
                </div>
                <div className="ct-grid">
                  <div className="fld">
                    <label htmlFor="f1">
                      회사명<em>*</em>
                    </label>
                    <input id="f1" required placeholder="회사명을 입력하세요" value={form.companyName} onChange={(e) => setField('companyName', e.target.value)} />
                  </div>
                  <div className="fld">
                    <label htmlFor="f2">
                      직원 수<em>*</em>
                    </label>
                    <select id="f2" required value={form.employeeCount} onChange={(e) => setField('employeeCount', e.target.value)}>
                      <option value="" disabled>
                        선택하세요
                      </option>
                      {EMPLOYEE_COUNTS.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="fld">
                    <label htmlFor="f3">
                      이름<em>*</em>
                    </label>
                    <input id="f3" required placeholder="이름을 입력하세요" value={form.name} onChange={(e) => setField('name', e.target.value)} />
                  </div>
                  <div className="fld">
                    <label htmlFor="f4">
                      연락처<em>*</em>
                    </label>
                    <input id="f4" type="tel" required placeholder="010-0000-0000"
                      value={form.phone}
                      onChange={(e) => setField('phone', formatPhone(e.target.value))} />
                  </div>
                  <div className="fld full">
                    <label htmlFor="f5">
                      이메일<em>*</em>
                    </label>
                    <input id="f5" type="email" required placeholder="name@company.com" value={form.email} onChange={(e) => setField('email', e.target.value)} />
                  </div>
                  <div className="fld full">
                    <label htmlFor="f6">
                      방문 경로<small>선택</small>
                    </label>
                    <select id="f6" value={form.visitRoute} onChange={(e) => setField('visitRoute', e.target.value)}>
                      <option value="" disabled>
                        어떻게 알게 되셨나요?
                      </option>
                      {VISIT_ROUTES.map((r) => (
                        <option key={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                  <div className="fld full">
                    <span className="l">
                      문의 유형<em>*</em>
                    </span>
                    <div className="chips">
                      {INQUIRY_TYPES.map((t) => (
                        <button type="button" key={t} className={inquiryType === t ? 'on' : undefined} onClick={() => setInquiryType(t)}>
                          <svg className="i">
                            <use href="#check" />
                          </svg>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="fld full">
                    <span className="l">
                      구축 환경<small>선택</small>
                    </span>
                    <div className="chips">
                      {ENVIRONMENTS.map((t) => (
                        <button type="button" key={t} className={environment === t ? 'on' : undefined} onClick={() => setEnvironment(t)}>
                          <svg className="i">
                            <use href="#check" />
                          </svg>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="fld full">
                    <label htmlFor="f8">
                      문의 내용<em>*</em>
                    </label>
                    <textarea
                      id="f8"
                      required
                      placeholder="AI를 쓰려는 업무, 필요한 기능, 연결할 시스템 등을 알려주세요."
                      value={form.message}
                      onChange={(e) => setField('message', e.target.value)}
                    />
                  </div>
                </div>
                <div className="agr">
                  <label>
                    <input type="checkbox" required checked={form.agreePrivacy} onChange={(e) => setField('agreePrivacy', e.target.checked)} />
                    <span className="cb">
                      <svg className="i">
                        <use href="#check" />
                      </svg>
                    </span>
                    <b>
                      개인정보 수집 및 이용 동의<small>(필수)</small>
                    </b>
                    <a href="#" aria-label="약관 보기">
                      <svg className="i">
                        <use href="#chev" />
                      </svg>
                    </a>
                  </label>
                  <label>
                    <input type="checkbox" checked={form.agreeMarketing} onChange={(e) => setField('agreeMarketing', e.target.checked)} />
                    <span className="cb">
                      <svg className="i">
                        <use href="#check" />
                      </svg>
                    </span>
                    <b>
                      마케팅 수신 및 활용 동의<small>(선택)</small>
                    </b>
                    <a href="#" aria-label="약관 보기">
                      <svg className="i">
                        <use href="#chev" />
                      </svg>
                    </a>
                  </label>
                </div>
                <p className="ct-note">
                  <em style={{ fontStyle: 'normal', color: 'var(--ac)' }}>*</em> 표시는 필수 입력 항목입니다.
                </p>
                {error && (
                  <p className="ct-note" role="alert" style={{ color: '#E5484D' }}>
                    {error}
                  </p>
                )}
                <div className="ct-sub">
                  <button className="btn p" type="submit" disabled={submitting}>
                    {submitting ? '보내는 중...' : '문의 보내기'}
                    <svg className="i">
                      <use href="#send" />
                    </svg>
                  </button>
                </div>
              </form>
              <div className="ct-done">
                <span className="ok">
                  <svg className="i">
                    <use href="#check" />
                  </svg>
                </span>
                <h2>문의가 접수되었습니다</h2>
                <p>영업일 기준 1일 이내에 담당자가 연락드립니다.</p>
                <button className="btn o" type="button" onClick={reset}>
                  새 문의 작성
                </button>
              </div>
            </div>

            <aside className="ct-side">
              <div className="ct-box pr-rv d1">
                <h3>문의 후 진행 과정</h3>
                <small>남겨주신 내용을 확인한 뒤 담당자가 연락드립니다.</small>
                <ol className="ct-steps">
                  <li>
                    <i>1</i>
                    <div>
                      <b>문의 접수</b>
                      <span>입력하신 연락처와 문의 내용이 담당자에게 전달됩니다.</span>
                    </div>
                  </li>
                  <li>
                    <i>2</i>
                    <div>
                      <b>담당자 연락</b>
                      <span>영업일 기준 1일 이내에 이메일 또는 전화로 연락드립니다.</span>
                    </div>
                  </li>
                  <li>
                    <i>3</i>
                    <div>
                      <b>맞춤 상담</b>
                      <span>업무 환경과 연결할 시스템에 맞춰 도입 방식을 안내합니다.</span>
                    </div>
                  </li>
                </ol>
              </div>
              <div className="ct-box pr-rv d2">
                <h3>바로 연락하기</h3>
                <div className="ct-ch">
                  <a href="mailto:cloud.mkt@etechsystem.co.kr">
                    <span className="ic">
                      <svg className="i">
                        <use href="#mail" />
                      </svg>
                    </span>
                    <span>
                      <small>이메일</small>
                      <b>cloud.mkt@etechsystem.co.kr</b>
                    </span>
                    <svg className="i">
                      <use href="#arr" />
                    </svg>
                  </a>
                  <a href="#" onClick={(e) => e.preventDefault()}>
                    <span className="ic">
                      <svg className="i">
                        <use href="#clock" />
                      </svg>
                    </span>
                    <span>
                      <small>상담 가능 시간</small>
                      <b>평일 09:00 – 18:00</b>
                    </span>
                    <svg className="i">
                      <use href="#arr" />
                    </svg>
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="sec" style={{ paddingTop: 40 }}>
        <div className="wrap">
          <div className="hd2 pr-rv">
            <div>
              <small>도입 전 궁금한 점을 알아보는 방법</small>
              <h2>도입 안내와 상담</h2>
            </div>
          </div>
          <div className="ct-gd">
            <a
              className="ct-gc pr-rv"
              href="#ctf"
              onClick={(e) => {
                e.preventDefault();
                scrollToForm('일반 문의');
              }}
            >
              <span className="ic" style={{ background: 'var(--pnk)' }}>
                <svg className="i">
                  <use href="#spark" />
                </svg>
              </span>
              <small>직접 사용해보는 AI 기능</small>
              <h4>무료 체험 신청</h4>
              <span className="ct-go">
                신청하기
                <svg className="i">
                  <use href="#arr" />
                </svg>
              </span>
            </a>
            <a
              className="ct-gc pr-rv d1"
              href="#ctf"
              onClick={(e) => {
                e.preventDefault();
                scrollToForm('소개자료');
              }}
            >
              <span className="ic" style={{ background: 'var(--blu)' }}>
                <svg className="i">
                  <use href="#doc" />
                </svg>
              </span>
              <small>제품 기능과 구성 안내</small>
              <h4>소개자료 받기</h4>
              <span className="ct-go">
                받기
                <svg className="i">
                  <use href="#arr" />
                </svg>
              </span>
            </a>
            <a
              className="ct-gc pr-rv d2"
              href="#ctf"
              onClick={(e) => {
                e.preventDefault();
                scrollToForm('도입 상담');
              }}
            >
              <span className="ic" style={{ background: 'var(--teal)' }}>
                <svg className="i">
                  <use href="#building" />
                </svg>
              </span>
              <small>설치 환경과 사내 시스템 연결 상담</small>
              <h4>기업 도입 문의</h4>
              <span className="ct-go">
                문의하기
                <svg className="i">
                  <use href="#arr" />
                </svg>
              </span>
            </a>
            {/* TODO: AI 챗봇 상담 연결 대상이 정해지면 href/onClick 교체 */}
            <a className="ct-gc pr-rv d3" href="#" onClick={(e) => e.preventDefault()}>
              <span className="ic" style={{ background: 'var(--grn)' }}>
                <svg className="i">
                  <use href="#msg" />
                </svg>
              </span>
              <small>제품과 이용 방법에 대한 답변</small>
              <h4>AI 챗봇 상담</h4>
              <span className="ct-go">
                상담 시작
                <svg className="i">
                  <use href="#arr" />
                </svg>
              </span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
